// Anonymous usage tracking. No personal data — just a random id in localStorage
// so we can count unique visitors, and a debounced count of input changes.

const SID_KEY = "practik_sid";

function sessionId() {
  try {
    let s = localStorage.getItem(SID_KEY);
    if (!s) {
      s =
        (window.crypto && crypto.randomUUID && crypto.randomUUID()) ||
        `s_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e9).toString(36)}`;
      localStorage.setItem(SID_KEY, s);
    }
    return s;
  } catch {
    return "anon"; // private mode / storage blocked
  }
}

function send(type, extra) {
  try {
    fetch("/api/event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type, sessionId: sessionId(), ...extra }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* analytics must never break the app */
  }
}

// Where this visitor came from: ?ref=<slug> on the landing URL (e.g. the
// practice-planner-reviews.uk CTAs use ?ref=ppr). Kept to a short slug so it
// can only ever be a campaign label, never anything identifying, and stored
// first-touch — a visitor who arrives via the reviews site and comes back
// directly still counts as referred.
const REF_KEY = "practik_ref";

function referral() {
  try {
    const raw = new URLSearchParams(window.location.search).get("ref");
    const slug = raw && raw.toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 24);
    if (slug) {
      localStorage.setItem(REF_KEY, slug);
      return slug;
    }
    return localStorage.getItem(REF_KEY) || undefined;
  } catch {
    return undefined; // private mode / storage blocked
  }
}

// A "session" is one browser session, not one page load: sessionStorage clears
// when the tab closes but survives a refresh, so reloading the page doesn't
// inflate the return count. Returning users are the metric that matters, so it
// needs to mean something.
const SESSION_KEY = "practik_session";

function isNewSession() {
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return false;
    sessionStorage.setItem(SESSION_KEY, "1");
    return true;
  } catch {
    return true; // storage blocked — count it, better than losing it
  }
}

let visited = false;
export function trackVisit() {
  if (visited) return; // once per page load
  visited = true;
  send("visit", { ref: referral(), newSession: isNewSession() });
}

// Reaching the result screen is the only moment Practik has actually delivered
// anything, so it's the denominator for every question worth asking: how many
// arrivals get a number, and how many of those go on to click the affiliate.
// Counted once per browser session — going back to change an answer and
// returning shouldn't read as a second result — and deduped again server-side
// for the "ever saw a result" per-person figure.
const RESULT_KEY = "practik_result";

export function trackResultView() {
  try {
    if (sessionStorage.getItem(RESULT_KEY)) return;
    sessionStorage.setItem(RESULT_KEY, "1");
  } catch {
    /* storage blocked — count it, better than losing it */
  }
  send("result_view");
}

// Opening the input screen — the landing CTA, or a ?start= deep link, which is
// why this is keyed on the screen rather than wired to the button. Splits the
// funnel in two: visit → start says whether the landing page persuades anyone,
// start → result says whether the three questions are answerable. Deduped per
// browser session like trackResultView, so going back doesn't count twice.
const START_KEY = "practik_start";

export function trackStart() {
  try {
    if (sessionStorage.getItem(START_KEY)) return;
    sessionStorage.setItem(START_KEY, "1");
  } catch {
    /* storage blocked — count it, better than losing it */
  }
  send("start");
}

// Affiliate clicks are counted server-side, by /go/<slug> itself (see
// server/index.js), for two reasons: that endpoint is also where the CTAs on
// practice-planner-reviews.uk land, and a redirect can't be dropped by a content
// blocker the way an /api/event beacon can. All the client adds is `?s=` naming
// the anonymous visitor, so a click can be tied to the session that made it.
// The id goes no further than our own redirect — /go/ answers with
// Referrer-Policy: no-referrer, so it never reaches the affiliate network.
export function affiliateHref(url) {
  if (!url || !url.startsWith("/go/")) return url;
  return `${url}?s=${encodeURIComponent(sessionId())}`;
}

// Debounce per field so dragging a slider counts as ONE change, not hundreds.
const timers = {};
const lastValue = {};
export function trackInputChange(field, value) {
  clearTimeout(timers[field]);
  timers[field] = setTimeout(() => {
    if (lastValue[field] !== value) {
      lastValue[field] = value;
      send("input_change", { field });
    }
  }, 600);
}
